<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCalendarStore } from 'dashboard/stores/calendar';
import { useRemindersStore } from 'dashboard/stores/reminders';
import { useNotificationsHub } from 'dashboard/composables/useNotificationsHub';
import { runCustomAutomations } from 'dashboard/composables/useCustomAutomationRunner';

import Button from 'dashboard/components-next/button/Button.vue';
import CalendarMonth from 'dashboard/components-next/Calendar/CalendarMonth.vue';
import EventCreateDialog from 'dashboard/components-next/Calendar/EventCreateDialog.vue';

const { t } = useI18n();
const store = useStore();
const calendarStore = useCalendarStore();
const remindersStore = useRemindersStore();
const { logActivity } = useNotificationsHub();

const eventWhen = evt =>
  `${evt.date}${evt.startTime ? ` ${evt.startTime}` : ''}${
    evt.endTime ? `–${evt.endTime}` : ''
  }`;

const pad = n => String(n).padStart(2, '0');

// Build the reminder's local 'YYYY-MM-DDTHH:mm' trigger from the event's start
// minus the chosen lead time (offset in minutes).
const reminderTriggerAt = (date, startTime, offsetMinutes) => {
  const start = new Date(`${date}T${startTime || '00:00'}`);
  start.setMinutes(start.getMinutes() - (offsetMinutes || 0));
  return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(
    start.getDate()
  )}T${pad(start.getHours())}:${pad(start.getMinutes())}`;
};

// Keep the event's linked reminder in sync with the dialog's reminder toggle.
const syncEventReminder = event => {
  if (event.reminderEnabled) {
    remindersStore.upsertForEvent(event.id, {
      title: event.title,
      description: event.description,
      triggerType: 'time',
      triggerAt: reminderTriggerAt(
        event.date,
        event.startTime,
        event.reminderOffset
      ),
      agentIds: [...(event.agentIds || [])],
      agents: [...(event.agents || [])],
      contacts: [...(event.contacts || [])],
      contactIds: [...(event.contactIds || [])],
      labels: [...(event.labels || [])],
      color: event.color,
    });
  } else {
    remindersStore.removeForEvent(event.id);
  }
};

// Context dispatched to the custom-automation engine for calendar events.
// days/hours/minutes are the TOTAL time-until-start expressed in each unit, so
// duration conditions like "minutes is_less_than 30" read naturally.
const calendarAutomationContext = evt => {
  const startMs = new Date(`${evt.date}T${evt.startTime || '00:00'}`).getTime();
  const totalMinutes = Math.max(0, Math.round((startMs - Date.now()) / 60000));
  return {
    title: evt.title,
    labels: evt.labels || [],
    agent_ids: evt.agentIds || [],
    contact_ids: evt.contactIds || (evt.contacts || []).map(c => c.id),
    starts_at: `${evt.date}T${evt.startTime || '00:00'}`,
    days: totalMinutes / 1440,
    hours: totalMinutes / 60,
    minutes: totalMinutes,
  };
};

const eventsByDate = computed(() => calendarStore.byDate);
const dialogRef = ref(null);

const openCreate = date => {
  dialogRef.value?.openForCreate(date);
};

const openEdit = event => {
  // Restore the "remind me" checkbox from any linked reminder.
  const linked = remindersStore.list.find(r => r.sourceEventId === event.id);
  dialogRef.value?.openForEdit({ ...event, reminderEnabled: Boolean(linked) });
};

const onSave = async payload => {
  if (payload.id) {
    const { id, ...patch } = payload;
    await calendarStore.update(id, patch);
    await syncEventReminder({ id, ...patch });
    runCustomAutomations(
      'calendar_event_updated',
      calendarAutomationContext(patch)
    );
    logActivity({
      category: 'event',
      action: 'updated',
      title: t('CALENDAR.ACTIVITY.UPDATED_TITLE', { title: patch.title }),
      body: eventWhen(patch),
      targetUserIds: patch.agentIds || [],
      meta: { eventId: id },
    });
    useAlert(t('CALENDAR.ALERTS.UPDATED'));
  } else {
    const { id, ...rest } = payload;
    const created = await calendarStore.create(rest);
    await syncEventReminder({
      ...created,
      reminderEnabled: rest.reminderEnabled,
      reminderOffset: rest.reminderOffset,
    });
    runCustomAutomations(
      'calendar_event_created',
      calendarAutomationContext(rest)
    );
    logActivity({
      category: 'event',
      action: 'created',
      title: t('CALENDAR.ACTIVITY.CREATED_TITLE', { title: rest.title }),
      body: eventWhen(rest),
      targetUserIds: rest.agentIds || [],
      meta: { eventId: created.id },
    });
    useAlert(t('CALENDAR.ALERTS.CREATED', { title: rest.title }));
  }
  dialogRef.value?.onSuccess();
};

const onRemove = async id => {
  const removed = calendarStore.getEvent(id);
  await calendarStore.remove(id);
  await remindersStore.removeForEvent(id);
  if (removed) {
    runCustomAutomations(
      'calendar_event_deleted',
      calendarAutomationContext(removed)
    );
  }
  logActivity({
    category: 'event',
    action: 'deleted',
    title: t('CALENDAR.ACTIVITY.DELETED_TITLE', { title: removed?.title || '' }),
    body: removed ? eventWhen(removed) : '',
    targetUserIds: removed?.agentIds || [],
    meta: { eventId: id },
  });
  dialogRef.value?.onSuccess();
  useAlert(t('CALENDAR.ALERTS.DELETED'));
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  calendarStore.fetch();
  remindersStore.fetch();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex flex-col">
        <h1 class="text-lg font-semibold text-n-slate-12 m-0">
          {{ t('CALENDAR.HEADER.TITLE') }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ t('CALENDAR.HEADER.SUBTITLE') }}
        </span>
      </div>
      <Button
        :label="t('CALENDAR.HEADER.NEW')"
        color="blue"
        size="sm"
        icon="i-lucide-plus"
        @click="openCreate(null)"
      />
    </div>

    <div class="flex-1 overflow-hidden p-6">
      <CalendarMonth
        :events-by-date="eventsByDate"
        @create="openCreate"
        @select="openEdit"
      />
    </div>

    <EventCreateDialog ref="dialogRef" @save="onSave" @remove="onRemove" />
  </div>
</template>
